import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../../../contexts/LanguageContext";
import { useAuth } from "../../../../contexts/AuthContext";
import { showSuccessToast, showErrorToast, showInfoToast } from "../../../../utils/toastNotifications";
import { ProcessingButtons } from "../../../../components/ProcessingButtons";

interface OrderStatusProps {
  formData: any;
  orderStatusForm: any;
  handleStatusFormInputChange: (event: any) => void;
  confirmPendingStatus: () => void;
  getExtraFieldLabel: () => string | null;
  getStatusBadge: (status: number) => JSX.Element;
  updatingStatus: boolean;
  messageStatus: string;
  confirmMessage: string;
  updateOrderStatus: () => void;
  onReload?: () => void;
}

export const OrderStatus: React.FC<OrderStatusProps> = ({
  formData,
  orderStatusForm,
  handleStatusFormInputChange,
  confirmPendingStatus,
  getExtraFieldLabel,
  getStatusBadge,
  updatingStatus,
  messageStatus,
  confirmMessage,
  updateOrderStatus,
  onReload,
}) => {
  const { t } = useTranslation('orders');
  const { isRTL } = useLanguage();
  const { hasPermission } = useAuth();
  const [voucherCode, setVoucherCode] = useState("");
  const [applyingVoucher, setApplyingVoucher] = useState(false);
  const [changingCustomerStatus, setChangingCustomerStatus] = useState(false);

  // Show toast when messageStatus changes
  useEffect(() => {
    if (messageStatus) {
      showInfoToast(messageStatus);
    }
  }, [messageStatus]);

  // Permission checks
  const canRedeemVoucher = hasPermission("Customer Voucher Redemption Order");
  const canApproveOrder = hasPermission("Customer Approval Order");

  const isInvoiceRelatedExtraField = () => {
    const status = Number(orderStatusForm.orderStatus);
    const paymentType = formData?.paymentType;

    return (
      ((status === 10 || status === 31) && (paymentType === 2 || paymentType === 1)) ||
      (status === 25 && (paymentType === 0 || paymentType === 1))
    );
  };

  const getInvoiceForCurrentStatus = () => {
    if (!Array.isArray(formData?.invoices) || formData.invoices.length === 0) {
      return null;
    }

    const status = Number(orderStatusForm.orderStatus);
    const paymentType = formData?.paymentType;
    const sortedByCreatedAtAsc = [...formData.invoices].sort(
      (a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

    // For partial payment, initial invoice-number status checks the first invoice,
    // while delivered invoice-number status checks the second invoice.
    if (paymentType === 1) {
      if (status === 10 || status === 31) {
        return sortedByCreatedAtAsc[0] ?? null;
      }

      if (status === 25) {
        return sortedByCreatedAtAsc[1] ?? sortedByCreatedAtAsc[0] ?? null;
      }
    }

    return sortedByCreatedAtAsc[sortedByCreatedAtAsc.length - 1] ?? null;
  };

  const shouldShowExtraField = () => {
    if (!getExtraFieldLabel()) {
      return false;
    }

    if (!isInvoiceRelatedExtraField()) {
      return true;
    }

    const targetInvoice = getInvoiceForCurrentStatus();
    return !targetInvoice?.isCollected;
  };

  // Handle voucher redemption by admin
  const handleRedeemVoucher = async () => {
    if (!voucherCode.trim()) {
      showErrorToast(t('customerActions.voucherRequired', 'Please enter a voucher code'));
      return;
    }

    setApplyingVoucher(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/adminorder/redeemvoucher`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          orderId: formData.id,
          voucherCode: voucherCode
        })
      });

      if (response.ok) {
        showSuccessToast(t('customerActions.voucherApplied', 'Voucher applied successfully'));
        setVoucherCode("");
        if (onReload) onReload();
      } else {
        const errorData = await response.json();
        showErrorToast(errorData.message || t('customerActions.voucherFailed', 'Failed to apply voucher'));
      }
    } catch (error) {
      showErrorToast(t('customerActions.voucherFailed', 'Failed to apply voucher'));
    } finally {
      setApplyingVoucher(false);
    }
  };

  // Handle customer status change by admin (approve/reject)
  const handleCustomerStatusChange = async (status: number) => {
    setChangingCustomerStatus(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/adminorder/changecustomerstatus`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          orderId: formData.id,
          orderStatus: status
        })
      });

      if (response.ok) {
        const statusText = status === 7 ?
          t('customerActions.orderApproved', 'Order approved on behalf of customer') :
          t('customerActions.orderRejected', 'Order rejected on behalf of customer');
        showSuccessToast(statusText);
        if (onReload) onReload();
      } else {
        const errorData = await response.json();
        showErrorToast(errorData.message || t('customerActions.statusChangeFailed', 'Failed to change status'));
      }
    } catch (error) {
      showErrorToast(t('customerActions.statusChangeFailed', 'Failed to change status'));
    } finally {
      setChangingCustomerStatus(false);
    }
  };

  // Show customer action panel when order is at status 6 (Approved - awaiting customer decision)
  if (formData.orderStatus === 6) {
    return (
      <div>
        <div className="row mb-4">
          <div className="col-md-4">
            <label className="form-label m-2">{t('fields.currentStatus')}</label>
            {getStatusBadge(formData.orderStatus)}
          </div>
        </div>

        {/* Customer Actions Panel - Only show if user has at least one permission */}
        {(canRedeemVoucher || canApproveOrder) && (
          <div className="card border-primary mb-4">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">
                <i className={`fa fa-user-check ${isRTL ? 'ms-2' : 'me-2'}`}></i>
                {t('customerActions.title', 'Customer Actions (Admin)')}
              </h5>
            </div>
            <div className="card-body">
              <div className="alert alert-info mb-4">
                <i className={`fa fa-info-circle ${isRTL ? 'ms-2' : 'me-2'}`}></i>
                {t('customerActions.description', 'This order is awaiting customer approval. You can perform these actions on behalf of the customer.')}
              </div>

              {/* Voucher Section - Only show if user has permission */}
              {canRedeemVoucher && (
                <div className="row mb-4">
                  <div className="col-12">
                    <h6 className="fw-bold mb-3">
                      <i className={`fa fa-ticket ${isRTL ? 'ms-2' : 'me-2'}`}></i>
                      {t('customerActions.applyVoucher', 'Apply Voucher')}
                    </h6>
                    <div className="row g-2 align-items-end">
                      <div className="col-md-4">
                        <label className="form-label">{t('customerActions.voucherCode', 'Voucher Code')}</label>
                        <input
                          type="text"
                          className="form-control"
                          value={voucherCode}
                          onChange={(e) => setVoucherCode(e.target.value)}
                          placeholder={t('customerActions.enterVoucherCode', 'Enter voucher code')}
                          disabled={applyingVoucher}
                        />
                      </div>
                      <div className="col-md-2">
                        <button
                          className="btn btn-success w-100"
                          onClick={handleRedeemVoucher}
                          disabled={applyingVoucher || !voucherCode.trim()}
                        >
                          {applyingVoucher ? (
                            <><i className="fa fa-spinner fa-spin me-2"></i>{t('actions.applying', 'Applying...')}</>
                          ) : (
                            <><i className="fa fa-check me-2"></i>{t('actions.apply', 'Apply')}</>
                          )}
                        </button>
                      </div>
                    </div>
                    {formData.voucherValue > 0 && (
                      <div className="mt-2">
                        <span className="badge bg-success">
                          {t('customerActions.voucherAppliedAmount', 'Voucher Applied')}: ${formData.voucherValue?.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Separator - Only show if both sections are visible */}
              {canRedeemVoucher && canApproveOrder && <hr />}

              {/* Approve/Reject Section - Only show if user has permission */}
              {canApproveOrder && (
                <div className="row">
                  <div className="col-12">
                    <h6 className="fw-bold mb-3">
                      <i className={`fa fa-check-circle ${isRTL ? 'ms-2' : 'me-2'}`}></i>
                      {t('customerActions.approveReject', 'Approve or Reject Order')}
                    </h6>
                    <p className="text-muted mb-3">
                      {t('customerActions.approveRejectDescription', 'Click one of the buttons below to approve or reject this order on behalf of the customer.')}
                    </p>
                    <div className={`d-flex gap-3 align-items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                      {/* While processing, hide both buttons and show a single spinner */}
                      <ProcessingButtons processing={changingCustomerStatus}>
                        <button
                          className="btn btn-success btn-lg"
                          onClick={() => handleCustomerStatusChange(7)}
                        >
                          <i className="fa fa-thumbs-up me-2"></i>{t('customerActions.approve', 'Approve Order')}
                        </button>
                        <button
                          className="btn btn-danger btn-lg"
                          onClick={() => handleCustomerStatusChange(8)}
                        >
                          <i className="fa fa-thumbs-down me-2"></i>{t('customerActions.reject', 'Reject Order')}
                        </button>
                      </ProcessingButtons>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (formData.orderStatus < 6) {
    return null;
  }

  return (
    <div>
      <div className="row">
        <div className="col-md-3">
          <label className="form-label">{t('fields.orderStatus')}</label>
          <select
            key={orderStatusForm.orderStatus}
            className="form-control"
            name="orderStatus"
            value={orderStatusForm.orderStatus}
            onChange={handleStatusFormInputChange}
          >
            <option>{t('refundTypes.0')}</option>

            {formData.orderStatus == 0 && (
              <>
                <option value={9}>{t('statusOptions.9')}</option>
                <option value={12}>{t('statusOptions.12')}</option>
              </>
            )}

            {formData.orderStatus == 7 && (
              <>
                <option value={10}>{t('statusOptions.10')}</option>
                <option value={11}>{t('statusOptions.11')}</option>
                <option value={12}>{t('statusOptions.12')}</option>
              </>
            )}

            {formData.orderStatus == 8 && (
              <>
                <option value={9}>{t('statusOptions.9')}</option>
                <option value={11}>{t('statusOptions.11')}</option>
                <option value={12}>{t('statusOptions.12')}</option>
              </>
            )}

            {formData.orderStatus == 9 && (
              <>
                <option value={10}>{t('statusOptions.10')}</option>
                <option value={12}>{t('statusOptions.12')}</option>
              </>
            )}

            {formData.orderStatus == 10 && (
              <>
                <option value={13}>{t('statusOptions.13')}</option>
                {/* <option value={11}>{t('statusOptions.11')}</option> */}
              </>
            )}

            {formData.orderStatus == 11 && (
              <>
                <option value={9}>{t('statusOptions.9')}</option>
                <option value={12}>{t('statusOptions.12')}</option>
              </>
            )}

            {formData.orderStatus == 13 && (
              <>
                <option value={14}>
                  {t('statusOptions.14')}
                </option>
                <option value={29}>{t('statusOptions.29')}</option>
              </>
            )}

            {formData.orderStatus == 14 && (
              <>
                <option value={15}>
                  {t('statusOptions.15')}
                </option>
                <option value={17}>{t('statusOptions.17')}</option>
                <option value={16}>{t('statusOptions.16')}</option>
              </>
            )}

            {formData.orderStatus == 15 && (
              <>
                <option value={20}>{t('statusOptions.20')}</option>
                <option value={16}>{t('statusOptions.16')}</option>
              </>
            )}

            {(formData.orderStatus == 16 ||
              formData.orderStatus == 29) &&
              formData.paymentType == 0 && (
                <>
                  <option value={12}>{t('statusOptions.12')}</option>
                </>
              )}

            {(formData.orderStatus == 16 ||
              formData.orderStatus == 29) &&
              (formData.paymentType == 2 || formData.paymentType == 1) &&
              orderStatusForm.refundType == 1 && (
                <>
                  <option value={30}>{t('statusOptions.30')}</option>
                </>
              )}

            {(formData.orderStatus == 16 ||
              formData.orderStatus == 29) &&
              (formData.paymentType == 2 || formData.paymentType == 1) &&
              orderStatusForm.refundType == 2 && (
                <>
                  <option value={18}>{t('statusOptions.18')}</option>
                </>
              )}
            {formData.orderStatus == 17 && (
              <>
                <option value={15}>{t('statusOptions.15')}</option>
                <option value={16}>{t('statusOptions.16')}</option>
              </>
            )}

            {formData.orderStatus == 20 && (
              <>
                <option value={16}>{t('statusOptions.16')}</option>
                <option value={21}>{t('statusOptions.21')}</option>
              </>
            )}

            {formData.orderStatus == 21 && (
              <>
                <option value={22}>{t('statusOptions.22')}</option>
              </>
            )}

            {formData.orderStatus == 22 && (
              <option value={23}>{t('statusOptions.23')}</option>
            )}

            {formData.orderStatus == 23 && (
              <>
                <option value={16}>{t('statusOptions.16')}</option>
                <option value={24}>{t('statusOptions.24')}</option>
              </>
            )}

            {formData.orderStatus == 24 && (
              <>
                <option value={25}>{t('statusOptions.25')}</option>
                <option value={26}>{t('statusOptions.26')}</option>
              </>
            )}

            {formData.orderStatus == 26 && (
              <>
                <option value={27}>
                  {t('statusOptions.27')}
                </option>
                <option value={28}>{t('statusOptions.28')}</option>
              </>
            )}

            {formData.orderStatus == 27 && (
              <>
                <option value={25}>{t('statusOptions.25')}</option>
                <option value={26}>{t('statusOptions.26')}</option>
              </>
            )}

            {formData.orderStatus == 30 && (
              <>
                <option value={18}>{t('statusOptions.18')}</option>
              </>
            )}
          </select>
        </div>
        {((formData.orderStatus == 16 || formData.orderStatus == 29) &&
          (formData.paymentType == 2 || formData.paymentType == 1)) && (
            <div className="col-md-3">
              <label className="form-label">{t('fields.refundType')}</label>
              <select
                key={orderStatusForm.refundType}
                className="form-control"
                name="refundType"
                value={orderStatusForm.refundType}
                onChange={handleStatusFormInputChange}
              >
                <option value={0}>{t('refundTypes.0')}</option>
                <option value={1}>{t('refundTypes.1')}</option>
                <option value={2}>{t('refundTypes.2')}</option>
              </select>
            </div>
          )}
        <div className="col-md-6">
          <label className="form-label">{t('fields.comment')}</label>
          <input
            type="text"
            className="form-control"
            name="comment"
            value={orderStatusForm.comment}
            onChange={handleStatusFormInputChange}
            placeholder={t('placeholders.enterComment')}
          />
        </div>

        {shouldShowExtraField() && (
          <div className="col-md-3">
            <label className="form-label">
              {getExtraFieldLabel()}
            </label>
            <input
              type="text"
              className="form-control"
              name="extraField"
              value={orderStatusForm.extraField}
              onChange={handleStatusFormInputChange}
            />
          </div>
        )}
        {getExtraFieldLabel() && isInvoiceRelatedExtraField() && shouldShowExtraField() && orderStatusForm.paymentGateways && orderStatusForm.paymentGateways.length > 0 && (
          <div className="col-md-3">
            <label className="form-label">
              {t('fields.paymentGateway', 'Payment Gateway')}
            </label>
            <select
              className="form-select"
              name="paymentGatewayId"
              value={orderStatusForm.paymentGatewayId || ""}
              onChange={handleStatusFormInputChange}
            >
              <option value="">{t('placeholders.selectPaymentGateway', 'Select Payment Gateway')}</option>
              {orderStatusForm.paymentGateways.map((gateway: any) => (
                <option key={gateway.id} value={gateway.id}>
                  {gateway.localizedName || gateway.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {formData.orderStatus != 2 &&
          formData.orderStatus != 4 &&
          formData.orderStatus != 12 &&
          formData.orderStatus != 18 &&
          formData.orderStatus != 25 &&
          formData.orderStatus != 29 &&
          !(orderStatusForm.orderStatus == 18 && orderStatusForm.refundType == 2) && (
            <>
              <div className="col-md-12 mt-2">
                <label className="form-label"> &nbsp; </label>
                <button
                  className={`btn btn-info ${isRTL ? 'ms-2' : 'me-2'}`}
                  onClick={confirmPendingStatus}
                  disabled={updatingStatus}
                >
                  {updatingStatus ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      {t('actions.updating')}
                    </>
                  ) : (
                    t('actions.saveStatus')
                  )}
                </button>
              </div>
            </>
          )}
        <div className="col-md-4">
          <label className="form-label m-2">{t('fields.currentStatus')}</label>
          {getStatusBadge(formData.orderStatus)}
        </div>
        <div className="col-md-4">
          <label className="form-label m-2">{t('fields.paymentStatus')}</label>
          {formData.invoices.some((x: any) => x.isCollected) ? (
            <span className={`badge bg-success m-2`}>
              {t('common.collected', 'Collected')}
            </span>
          ) : (
            <span className={`badge bg-danger m-2`}>
              {t('common.notCollected', 'Not Collected')}
            </span>
          )}
        </div>
      </div>

      {/* Voucher Refund Approval Inline Form */}
      {orderStatusForm.orderStatus == 18 && orderStatusForm.refundType == 2 && formData.orderStatus != 18 && (
        <div className="row mt-4">
          <div className="col-12">
            <div className="card border-warning">
              <div className="card-header bg-warning text-dark">
                <h5 className="mb-0">
                  <i className="fa fa-exclamation-triangle me-2"></i>
                  {t('voucherApproval.title', 'Voucher Refund Approval Required')}
                </h5>
              </div>
              <div className="card-body">
                <div className="alert alert-info mb-3">
                  <i className="fa fa-info-circle me-2"></i>
                  {t('voucherApproval.message', 'Do you confirm sending voucher to the customer with amount')}
                  <strong className="mx-1">
                    ${formData.paymentType == 1 && formData.initialPartialPayment
                      ? formData.initialPartialPayment.toFixed(2)
                      : (formData.totalPrice - (formData.voucherValue || 0)).toFixed(2)
                    }
                  </strong>?
                  {formData.paymentType == 1 && formData.initialPartialPayment && (
                    <div className="mt-2">
                      <small className="text-muted">
                        <i className="fa fa-info-circle me-1"></i>
                        {t('voucherApproval.partialPaymentNote', 'This amount represents the initial partial payment made by the customer.')}
                      </small>
                    </div>
                  )}
                </div>
                <div className={`d-flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={updateOrderStatus}
                    disabled={updatingStatus}
                  >
                    {updatingStatus ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {t('actions.updating')}
                      </>
                    ) : (
                      <>
                        <i className="fa fa-check me-2"></i>
                        {t('voucherApproval.approve', 'Yes, Send Voucher')}
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      // Reset the status selection
                      const event = {
                        target: {
                          name: 'orderStatus',
                          value: 0
                        }
                      };
                      handleStatusFormInputChange(event);
                    }}
                  >
                    <i className="fa fa-times me-2"></i>
                    {t('voucherApproval.cancel', 'Cancel')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Confirmation Modal */}
      <div
        className="modal fade"
        id="confirmPendingModal"
        tabIndex={-1}
        aria-labelledby="confirmPendingModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className={`modal-header ${isRTL ? 'flex-row-reverse' : ''}`}>
              <h5
                className="modal-title text-black"
                id="confirmPendingModalLabel"
              >
                {t('modal.confirmStatusChange')}
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label={t('common.close', 'Close')}
              ></button>
            </div>
            <div className="modal-body text-black">{confirmMessage}</div>
            <div className={`modal-footer ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                {t('actions.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={updateOrderStatus}
                disabled={updatingStatus}
              >
                {updatingStatus ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {t('actions.updating')}
                  </>
                ) : (
                  t('modal.yesChange')
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
